// lib/permissions/PermissionsDialog.tsx
import {
  DeleteOutlineOutlined,
  PersonOutlineOutlined,
  GroupOutlined
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import React from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var PermissionsDialog = ({
  open,
  title,
  onClose,
  permissionApi,
  userApi,
  groupApi
}) => {
  const [permissions, setPermissions] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [groups, setGroups] = React.useState([]);
  const [tab, setTab] = React.useState(0);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [selectedGroup, setSelectedGroup] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  React.useEffect(() => {
    if (open) {
      setLoading(true);
      setErrorMessage(null);
      void Promise.all([
        permissionApi.all(),
        userApi.fetch.all(),
        groupApi.fetch.all()
      ]).then(([perms, u, g]) => {
        setPermissions(perms);
        setUsers(u);
        setGroups(g);
      }).catch(() => {
        setErrorMessage("You do not have access to manage permissions.");
      }).finally(() => setLoading(false));
    }
  }, [open, permissionApi, userApi, groupApi]);
  const handleAddUser = async () => {
    if (!selectedUser) return;
    const next = [
      ...permissions,
      { type: "user", userId: selectedUser.id }
    ];
    try {
      await permissionApi.update(next);
      setPermissions(next);
      setSelectedUser(null);
    } catch (err) {
      console.error("Failed to add user permission", err);
    }
  };
  const handleAddGroup = async () => {
    if (!selectedGroup) return;
    const next = [
      ...permissions,
      { type: "group", groupId: selectedGroup.id }
    ];
    try {
      await permissionApi.update(next);
      setPermissions(next);
      setSelectedGroup(null);
    } catch (err) {
      console.error("Failed to add group permission", err);
    }
  };
  const handleDelete = async (index) => {
    const next = permissions.filter((_, i) => i !== index);
    try {
      await permissionApi.update(next);
      setPermissions(next);
    } catch (err) {
      console.error("Failed to remove permission", err);
    }
  };
  const userPerms = permissions.filter(
    (p) => p.type === "user"
  );
  const groupPerms = permissions.filter(
    (p) => p.type === "group"
  );
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      open,
      onClose,
      maxWidth: "sm",
      fullWidth: true,
      disableRestoreFocus: true,
      children: [
        /* @__PURE__ */ jsx(DialogTitle, { children: title }),
        /* @__PURE__ */ jsx(DialogContent, { sx: { minHeight: 400 }, children: loading ? /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400
            },
            children: /* @__PURE__ */ jsx(CircularProgress, {})
          }
        ) : /* @__PURE__ */ jsxs(Fragment, { children: [
          errorMessage != null && /* @__PURE__ */ jsx(Typography, { color: "error", sx: { mb: 2 }, children: errorMessage }),
          /* @__PURE__ */ jsxs(Tabs, { value: tab, onChange: (_e, v) => setTab(v), sx: { mb: 2 }, children: [
            /* @__PURE__ */ jsx(Tab, { label: `Users (${userPerms.length})` }),
            /* @__PURE__ */ jsx(Tab, { label: `Groups (${groupPerms.length})` })
          ] }),
          tab === 0 && /* @__PURE__ */ jsxs(Box, { children: [
            /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", gap: 1, mb: 2 }, children: [
              /* @__PURE__ */ jsx(
                Autocomplete,
                {
                  options: users.filter(
                    (u) => !userPerms.some((p) => p.userId === u.id)
                  ),
                  value: selectedUser,
                  getOptionLabel: (u) => `${u.display} (${u.name})`,
                  onChange: (_e, v) => setSelectedUser(v),
                  renderInput: (params) => /* @__PURE__ */ jsx(TextField, { ...params, label: "Add User", size: "small" }),
                  sx: { flexGrow: 1 }
                }
              ),
              /* @__PURE__ */ jsx(
                Button,
                {
                  variant: "contained",
                  onClick: () => void handleAddUser(),
                  disabled: !selectedUser,
                  children: "Add"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs(List, { children: [
              userPerms.map((p) => {
                const u = users.find((user) => user.id === p.userId);
                const realIndex = permissions.indexOf(p);
                return /* @__PURE__ */ jsxs(ListItem, { divider: true, children: [
                  /* @__PURE__ */ jsx(
                    PersonOutlineOutlined,
                    {
                      sx: { mr: 2, color: "text.secondary" }
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    ListItemText,
                    {
                      primary: u?.display || p.userId,
                      secondary: u?.email || ""
                    }
                  ),
                  /* @__PURE__ */ jsx(ListItemSecondaryAction, { children: /* @__PURE__ */ jsx(
                    IconButton,
                    {
                      edge: "end",
                      color: "error",
                      onClick: () => void handleDelete(realIndex),
                      children: /* @__PURE__ */ jsx(DeleteOutlineOutlined, { fontSize: "small" })
                    }
                  ) })
                ] }, p.userId);
              }),
              userPerms.length === 0 && /* @__PURE__ */ jsx(
                Typography,
                {
                  variant: "body2",
                  color: "text.disabled",
                  sx: { textAlign: "center", py: 2 },
                  children: "No user permissions"
                }
              )
            ] })
          ] }),
          tab === 1 && /* @__PURE__ */ jsxs(Box, { children: [
            /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", gap: 1, mb: 2 }, children: [
              /* @__PURE__ */ jsx(
                Autocomplete,
                {
                  options: groups.filter(
                    (g) => !groupPerms.some((p) => p.groupId === g.id)
                  ),
                  value: selectedGroup,
                  getOptionLabel: (g) => g.name,
                  onChange: (_e, v) => setSelectedGroup(v),
                  renderInput: (params) => /* @__PURE__ */ jsx(TextField, { ...params, label: "Add Group", size: "small" }),
                  sx: { flexGrow: 1 }
                }
              ),
              /* @__PURE__ */ jsx(
                Button,
                {
                  variant: "contained",
                  onClick: () => void handleAddGroup(),
                  disabled: !selectedGroup,
                  children: "Add"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs(List, { children: [
              groupPerms.map((p) => {
                const g = groups.find((group) => group.id === p.groupId);
                const realIndex = permissions.indexOf(p);
                return /* @__PURE__ */ jsxs(ListItem, { divider: true, children: [
                  /* @__PURE__ */ jsx(
                    GroupOutlined,
                    {
                      sx: { mr: 2, color: "text.secondary" }
                    }
                  ),
                  /* @__PURE__ */ jsx(ListItemText, { primary: g?.name || p.groupId }),
                  /* @__PURE__ */ jsx(ListItemSecondaryAction, { children: /* @__PURE__ */ jsx(
                    IconButton,
                    {
                      edge: "end",
                      color: "error",
                      onClick: () => void handleDelete(realIndex),
                      children: /* @__PURE__ */ jsx(DeleteOutlineOutlined, { fontSize: "small" })
                    }
                  ) })
                ] }, p.groupId);
              }),
              groupPerms.length === 0 && /* @__PURE__ */ jsx(
                Typography,
                {
                  variant: "body2",
                  color: "text.disabled",
                  sx: { textAlign: "center", py: 2 },
                  children: "No group permissions"
                }
              )
            ] })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(DialogActions, { children: /* @__PURE__ */ jsx(Button, { onClick: onClose, children: "Close" }) })
      ]
    }
  );
};

export {
  PermissionsDialog
};
